<template>
    <div>
        <div class="row b-b">
            <div class="col-12 p-3">
                <company-form-token></company-form-token>
            </div>
        </div>
        <div class="row">
            <div class="col-12 p-3">
                <info-alert-box alert-type="warning"
                                text="Please note that some MCP tool usage (SMS, RVM, calls, number lookup) is considered automated & paid. You are agreeing to our billing terms by using MCP tools."
                                kb-link="https://support.aloware.com/en/articles/9033804-aloware-pricing-plans-empowering-your-teams-with-flexible-solutions">
                </info-alert-box>

                <div class="mb-2">
                    <strong class="integration-api-titles">Model Context Protocol (MCP)</strong>
                </div>
                <p class="text-md text-muted">
                    MCP allows AI assistants and MCP-compatible clients to interact
                    with your Aloware account directly. Connect your AI assistant to Aloware and it will be
                    able to manage contacts, send communications, enroll contacts in sequences, manage power
                    dialer lists, and more.
                </p>

                <hr>

                <!-- ══════════════ Authentication ══════════════ -->

                <div class="mb-2">
                    <strong class="integration-api-titles">Authentication</strong>
                </div>
                <p class="text-muted mb-3">
                    Aloware MCP supports two authentication methods. Choose the one that best fits your client.
                </p>

                <div class="mcp-auth-cards">
                    <div class="mcp-auth-card mcp-auth-card--recommended">
                        <div class="mcp-auth-card__badge">Recommended</div>
                        <div class="mcp-auth-card__icon">
                            <i class="fa fa-shield-alt"></i>
                        </div>
                        <strong>OAuth 2.1</strong>
                        <p class="text-muted mt-1 mb-0">
                            Simply provide the MCP server URL — the client handles login and authorization automatically.
                            No tokens to copy or manage.
                        </p>
                    </div>

                    <div class="mcp-auth-card">
                        <div class="mcp-auth-card__icon">
                            <i class="fa fa-key"></i>
                        </div>
                        <strong>Bearer Token</strong>
                        <p class="text-muted mt-1 mb-0">
                            For clients without OAuth support, use your API token in the Authorization header.
                        </p>
                    </div>
                </div>

                <hr>

                <!-- ══════════════ MCP Servers ══════════════ -->

                <div class="mb-2">
                    <strong class="integration-api-titles">MCP Servers</strong>
                </div>
                <p class="text-muted mb-3">
                    Use the unified Aloware server below to access every tool through a single endpoint.
                    The per-domain servers remain available for backward compatibility with existing integrations.
                </p>

                <div class="mcp-server-cards" v-if="!loading_whitelabel">
                    <!-- Unified Aloware Server (recommended) -->
                    <div class="mcp-server-card mcp-server-card--recommended">
                        <div class="mcp-server-card__badge">Recommended</div>
                        <div class="mcp-server-card__header">
                            <div>
                                <div class="mcp-server-card__name">
                                    <i class="fa fa-bolt mcp-server-card__icon"></i>
                                    Aloware (all tools)
                                </div>
                                <p class="mcp-server-card__desc">
                                    Single endpoint exposing every Aloware tool — communication, contacts, and operations.
                                    Recommended for new integrations.
                                </p>
                            </div>
                        </div>
                        <div class="mcp-server-card__url-row">
                            <code class="mcp-server-card__url">{{ serverUrl('') }}</code>
                            <button class="mcp-copy-btn"
                                    v-clipboard:copy="serverUrl('')"
                                    v-clipboard:success="onCopySuccess"
                                    v-clipboard:error="onCopyError"
                                    title="Copy URL">
                                <i class="fa fa-copy"></i>
                            </button>
                        </div>
                        <button class="mcp-server-card__toggle"
                                @click="toggleTools('aloware')">
                            <span>{{ expandedServers.aloware ? 'Hide' : 'Show' }} tools (14)</span>
                            <i class="fa"
                               :class="expandedServers.aloware ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                        </button>
                        <transition name="mcp-slide">
                            <div v-if="expandedServers.aloware" class="mcp-server-card__tools">
                                <div class="mcp-tool-group">Communication</div>
                                <div class="mcp-tool">
                                    <strong class="mcp-tool__name">send-sms</strong>
                                    <span class="mcp-tool__desc">
                                        Send an SMS or MMS message to a phone number via an Aloware line.
                                    </span>
                                </div>
                                <div class="mcp-tool">
                                    <strong class="mcp-tool__name">send-rvm</strong>
                                    <span class="mcp-tool__desc">Send a ringless voicemail.</span>
                                </div>
                                <div class="mcp-tool">
                                    <strong class="mcp-tool__name">establish-two-legged-call</strong>
                                    <span class="mcp-tool__desc">Connect a contact with a user or ring group.</span>
                                </div>
                                <div class="mcp-tool">
                                    <strong class="mcp-tool__name">make-alo-ai-outbound-call</strong>
                                    <span class="mcp-tool__desc">Make an outbound call using an AloAi voice agent.</span>
                                </div>

                                <div class="mcp-tool-group">Contact</div>
                                <div class="mcp-tool">
                                    <strong class="mcp-tool__name">create-lead</strong>
                                    <span class="mcp-tool__desc">Create or update a contact.</span>
                                </div>
                                <div class="mcp-tool">
                                    <strong class="mcp-tool__name">contact-lookup</strong>
                                    <span class="mcp-tool__desc">Look up a contact by phone number.</span>
                                </div>
                                <div class="mcp-tool">
                                    <strong class="mcp-tool__name">enroll-contact-to-sequence</strong>
                                    <span class="mcp-tool__desc">Enroll a contact into a sequence.</span>
                                </div>
                                <div class="mcp-tool">
                                    <strong class="mcp-tool__name">disenroll-contact-from-sequence</strong>
                                    <span class="mcp-tool__desc">Disenroll a contact from all sequences.</span>
                                </div>
                                <div class="mcp-tool">
                                    <strong class="mcp-tool__name">number-lookup</strong>
                                    <span class="mcp-tool__desc">Carrier/LRN lookup. Paid operation.</span>
                                </div>

                                <div class="mcp-tool-group">Operations</div>
                                <div class="mcp-tool">
                                    <strong class="mcp-tool__name">get-users</strong>
                                    <span class="mcp-tool__desc">Retrieve all users/agents with their current status.</span>
                                </div>
                                <div class="mcp-tool">
                                    <strong class="mcp-tool__name">remove-contact-from-power-dialer</strong>
                                    <span class="mcp-tool__desc">Remove a contact from all Power Dialer lists.</span>
                                </div>
                                <div class="mcp-tool">
                                    <strong class="mcp-tool__name">clear-power-dialer-list</strong>
                                    <span class="mcp-tool__desc">Clear all contacts from a Power Dialer list.</span>
                                </div>
                                <div class="mcp-tool">
                                    <strong class="mcp-tool__name">clear-power-dialer-user-lists</strong>
                                    <span class="mcp-tool__desc">Clear all Power Dialer lists for a user.</span>
                                </div>
                                <div class="mcp-tool">
                                    <strong class="mcp-tool__name">inbox-availability</strong>
                                    <span class="mcp-tool__desc">Check user availability for a ring group.</span>
                                </div>
                            </div>
                        </transition>
                    </div>

                    <div class="mcp-legacy-heading">
                        Legacy per-domain servers
                        <span class="mcp-legacy-heading__hint">— kept for backward compatibility</span>
                    </div>

                    <!-- Communication Server -->
                    <div class="mcp-server-card">
                        <div class="mcp-server-card__header">
                            <div>
                                <div class="mcp-server-card__name">
                                    <i class="fa fa-comments mcp-server-card__icon"></i>
                                    Communication
                                </div>
                                <p class="mcp-server-card__desc">
                                    Send SMS/MMS, ringless voicemails, establish calls, and make AloAi outbound calls.
                                </p>
                            </div>
                        </div>
                        <div class="mcp-server-card__url-row">
                            <code class="mcp-server-card__url">{{ serverUrl('communication') }}</code>
                            <button class="mcp-copy-btn"
                                    v-clipboard:copy="serverUrl('communication')"
                                    v-clipboard:success="onCopySuccess"
                                    v-clipboard:error="onCopyError"
                                    title="Copy URL">
                                <i class="fa fa-copy"></i>
                            </button>
                        </div>
                        <button class="mcp-server-card__toggle"
                                @click="toggleTools('communication')">
                            <span>{{ expandedServers.communication ? 'Hide' : 'Show' }} tools (4)</span>
                            <i class="fa"
                               :class="expandedServers.communication ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                        </button>
                        <transition name="mcp-slide">
                            <div v-if="expandedServers.communication" class="mcp-server-card__tools">
                                <div class="mcp-tool">
                                    <strong class="mcp-tool__name">send-sms</strong>
                                    <span class="mcp-tool__desc">
                                        Send an SMS or MMS message to a phone number via an Aloware line.
                                        Requires <code>to</code>, <code>message</code>, and <code>from</code> or <code>line_id</code>.
                                        Optional: <code>image_url</code>, <code>user_id</code>, <code>force_random</code>.
                                    </span>
                                </div>
                                <div class="mcp-tool">
                                    <strong class="mcp-tool__name">send-rvm</strong>
                                    <span class="mcp-tool__desc">
                                        Send a ringless voicemail. Requires <code>to</code>, <code>audio_url</code>,
                                        and <code>from</code> or <code>line_id</code>.
                                    </span>
                                </div>
                                <div class="mcp-tool">
                                    <strong class="mcp-tool__name">establish-two-legged-call</strong>
                                    <span class="mcp-tool__desc">
                                        Connect a contact with a user or ring group.
                                        Requires contact identifier, user/ring group, and line.
                                    </span>
                                </div>
                                <div class="mcp-tool">
                                    <strong class="mcp-tool__name">make-alo-ai-outbound-call</strong>
                                    <span class="mcp-tool__desc">
                                        Make an outbound call using an AloAi voice agent.
                                        Requires <code>bot_id</code> and contact identifier.
                                    </span>
                                </div>
                            </div>
                        </transition>
                    </div>

                    <!-- Contact Server -->
                    <div class="mcp-server-card">
                        <div class="mcp-server-card__header">
                            <div>
                                <div class="mcp-server-card__name">
                                    <i class="fa fa-address-book mcp-server-card__icon"></i>
                                    Contact
                                </div>
                                <p class="mcp-server-card__desc">
                                    Create and look up contacts, manage sequences, and perform number lookups.
                                </p>
                            </div>
                        </div>
                        <div class="mcp-server-card__url-row">
                            <code class="mcp-server-card__url">{{ serverUrl('contact') }}</code>
                            <button class="mcp-copy-btn"
                                    v-clipboard:copy="serverUrl('contact')"
                                    v-clipboard:success="onCopySuccess"
                                    v-clipboard:error="onCopyError"
                                    title="Copy URL">
                                <i class="fa fa-copy"></i>
                            </button>
                        </div>
                        <button class="mcp-server-card__toggle"
                                @click="toggleTools('contact')">
                            <span>{{ expandedServers.contact ? 'Hide' : 'Show' }} tools (5)</span>
                            <i class="fa"
                               :class="expandedServers.contact ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                        </button>
                        <transition name="mcp-slide">
                            <div v-if="expandedServers.contact" class="mcp-server-card__tools">
                                <div class="mcp-tool">
                                    <strong class="mcp-tool__name">create-lead</strong>
                                    <span class="mcp-tool__desc">
                                        Create or update a contact. Requires <code>phone_number</code> (E.164).
                                        Optional: name, email, company, owner, sequence, tags, address fields.
                                    </span>
                                </div>
                                <div class="mcp-tool">
                                    <strong class="mcp-tool__name">contact-lookup</strong>
                                    <span class="mcp-tool__desc">
                                        Look up a contact by phone number. Returns the full contact record.
                                    </span>
                                </div>
                                <div class="mcp-tool">
                                    <strong class="mcp-tool__name">enroll-contact-to-sequence</strong>
                                    <span class="mcp-tool__desc">
                                        Enroll a contact into a sequence.
                                        Requires <code>sequence_id</code> and <code>source</code>.
                                    </span>
                                </div>
                                <div class="mcp-tool">
                                    <strong class="mcp-tool__name">disenroll-contact-from-sequence</strong>
                                    <span class="mcp-tool__desc">
                                        Disenroll a contact from all sequences.
                                        Requires <code>source</code> and contact identifier.
                                    </span>
                                </div>
                                <div class="mcp-tool">
                                    <strong class="mcp-tool__name">number-lookup</strong>
                                    <span class="mcp-tool__desc">
                                        Carrier/LRN lookup. Returns line type, carrier, and location. Paid operation.
                                    </span>
                                </div>
                            </div>
                        </transition>
                    </div>

                    <!-- Operations Server -->
                    <div class="mcp-server-card">
                        <div class="mcp-server-card__header">
                            <div>
                                <div class="mcp-server-card__name">
                                    <i class="fa fa-cogs mcp-server-card__icon"></i>
                                    Operations
                                </div>
                                <p class="mcp-server-card__desc">
                                    Retrieve users, manage power dialer lists, and check inbox availability.
                                </p>
                            </div>
                        </div>
                        <div class="mcp-server-card__url-row">
                            <code class="mcp-server-card__url">{{ serverUrl('operations') }}</code>
                            <button class="mcp-copy-btn"
                                    v-clipboard:copy="serverUrl('operations')"
                                    v-clipboard:success="onCopySuccess"
                                    v-clipboard:error="onCopyError"
                                    title="Copy URL">
                                <i class="fa fa-copy"></i>
                            </button>
                        </div>
                        <button class="mcp-server-card__toggle"
                                @click="toggleTools('operations')">
                            <span>{{ expandedServers.operations ? 'Hide' : 'Show' }} tools (4)</span>
                            <i class="fa"
                               :class="expandedServers.operations ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                        </button>
                        <transition name="mcp-slide">
                            <div v-if="expandedServers.operations" class="mcp-server-card__tools">
                                <div class="mcp-tool">
                                    <strong class="mcp-tool__name">get-users</strong>
                                    <span class="mcp-tool__desc">
                                        Retrieve all users/agents with their current status.
                                    </span>
                                </div>
                                <div class="mcp-tool">
                                    <strong class="mcp-tool__name">remove-contact-from-power-dialer</strong>
                                    <span class="mcp-tool__desc">
                                        Remove a contact from all Power Dialer lists. Requires <code>contact_id</code>.
                                    </span>
                                </div>
                                <div class="mcp-tool">
                                    <strong class="mcp-tool__name">clear-power-dialer-list</strong>
                                    <span class="mcp-tool__desc">
                                        Clear all contacts from a Power Dialer list. Requires <code>list_id</code>.
                                    </span>
                                </div>
                                <div class="mcp-tool">
                                    <strong class="mcp-tool__name">clear-power-dialer-user-lists</strong>
                                    <span class="mcp-tool__desc">
                                        Clear all Power Dialer lists for a user. Requires <code>user_id</code>.
                                    </span>
                                </div>
                                <div class="mcp-tool">
                                    <strong class="mcp-tool__name">inbox-availability</strong>
                                    <span class="mcp-tool__desc">
                                        Check user availability for a ring group. Requires <code>ring_group_id</code>.
                                    </span>
                                </div>
                            </div>
                        </transition>
                    </div>
                </div>

                <hr>

                <!-- ══════════════ Client Configuration ══════════════ -->

                <div class="mb-2">
                    <strong class="integration-api-titles">Client Configuration</strong>
                </div>
                <p class="text-muted mb-3">
                    Choose your authentication method, then copy the configuration for your client.
                </p>

                <div class="mcp-config-tabs mb-3">
                    <button class="mcp-config-tab"
                            :class="{ 'mcp-config-tab--active': configTab === 'oauth' }"
                            @click="configTab = 'oauth'">
                        OAuth 2.1
                    </button>
                    <button class="mcp-config-tab"
                            :class="{ 'mcp-config-tab--active': configTab === 'bearer' }"
                            @click="configTab = 'bearer'">
                        Bearer Token
                    </button>
                </div>

                <!-- OAuth config -->
                <div v-if="configTab === 'oauth'" v-show="!loading_whitelabel">
                    <p class="text-muted mb-2">
                        If your client supports OAuth, simply paste a server URL from above.
                        For JSON-based configuration, add the following to your client's MCP config file:
                    </p>
                    <div class="mcp-code-block">
                        <button class="mcp-code-block__copy"
                                v-clipboard:copy="oauthConfigJson"
                                v-clipboard:success="onCopySuccess"
                                v-clipboard:error="onCopyError"
                                title="Copy JSON">
                            <i class="fa fa-copy"></i>
                        </button>
                        <pre v-highlightjs :key="renderRefreshToken + '_mcp_oauth_' + baseUrl"><code class="json">{{ oauthConfigJson }}</code></pre>
                    </div>
                </div>

                <!-- Bearer config -->
                <div v-if="configTab === 'bearer'" v-show="!loading_whitelabel">
                    <p class="text-muted mb-2">
                        Add the following to your client's MCP config file:
                    </p>
                    <div class="mcp-code-block">
                        <button class="mcp-code-block__copy"
                                v-clipboard:copy="bearerConfigJson"
                                v-clipboard:success="onCopySuccess"
                                v-clipboard:error="onCopyError"
                                title="Copy JSON">
                            <i class="fa fa-copy"></i>
                        </button>
                        <pre v-highlightjs :key="renderRefreshToken + '_mcp_bearer_' + baseUrl"><code class="json">{{ bearerConfigJson }}</code></pre>
                    </div>
                </div>

                <p class="_600 mt-3"
                   v-if="!statics.whitelabel">
                    If you need more API functions, please contact our support at support@aloware.com .
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import { company_token_mixin } from '../../mixins';
import CompanyFormToken from '../account/company-form-token';
import InfoAlertBox from "../info-alert-box.vue";

export default {
    name: "integrations-mcp",

    mixins: [
        company_token_mixin
    ],

    components: {
        InfoAlertBox,
        CompanyFormToken
    },

    data() {
        return {
            statics: {
                domain: null,
                whitelabel: false,
            },
            loading_whitelabel: true,
            configTab: 'oauth',
            expandedServers: {
                aloware: false,
                communication: false,
                contact: false,
                operations: false,
            },
        }
    },

    computed: {
        baseUrl() {
            if (!this.statics.domain) return ''
            return this.$options.filters.fixDomain(this.statics.domain)
        },

        oauthConfigJson() {
            return JSON.stringify({
                mcpServers: {
                    aloware: { url: this.serverUrl('') },
                }
            }, null, 4)
        },

        bearerConfigJson() {
            const token = this.companyToken
            const headers = { Authorization: `Bearer ${token}` }
            return JSON.stringify({
                mcpServers: {
                    aloware: { url: this.serverUrl(''), headers },
                }
            }, null, 4)
        },
    },

    created() {
        this.getStatics()
    },

    methods: {
        getStatics() {
            this.loading_whitelabel = true
            axios.get('/get-statics')
                .then(res => {
                    this.statics = res.data
                    this.loading_whitelabel = false
                })
                .catch(err => {
                    console.log(err)
                    this.$root.handleErrors(err.response)
                    this.loading_whitelabel = false
                })
        },

        serverUrl(server) {
            return server
                ? `${this.baseUrl}/mcp/${server}`
                : `${this.baseUrl}/mcp`
        },

        toggleTools(server) {
            this.expandedServers[server] = !this.expandedServers[server]
        },

        onCopySuccess() {
            this.$notify({
                message: 'Copied to clipboard.',
                type: 'success',
            })
        },

        onCopyError() {
            this.$notify({
                message: 'Failed to copy to clipboard.',
                type: 'error',
            })
        },
    }
}
</script>

<style scoped>
/* ── Auth cards ── */
.mcp-auth-cards {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1rem;
}

.mcp-auth-card {
    position: relative;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 1.25rem;
}

.mcp-auth-card--recommended {
    border-color: #03c04c;
    background: #f0fdf4;
}

.mcp-auth-card__badge {
    position: absolute;
    top: -8px;
    right: 12px;
    background: #03c04c;
    color: #fff;
    font-size: 0.625rem;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 3px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.mcp-auth-card__icon {
    font-size: 1.25rem;
    color: #6b7280;
    margin-bottom: 0.5rem;
}

.mcp-auth-card--recommended .mcp-auth-card__icon {
    color: #03c04c;
}

/* ── Server cards ── */
.mcp-server-cards {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.mcp-server-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    overflow: hidden;
    position: relative;
}

.mcp-server-card--recommended {
    border-color: #03c04c;
    box-shadow: 0 0 0 1px #03c04c;
    overflow: visible;
    margin-top: 8px;
}

.mcp-server-card__badge {
    position: absolute;
    top: -8px;
    right: 12px;
    background: #03c04c;
    color: #fff;
    font-size: 0.625rem;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 3px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.mcp-legacy-heading {
    margin-top: 0.5rem;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #6b7280;
}

.mcp-legacy-heading__hint {
    font-weight: 400;
    color: #9ca3af;
}

.mcp-tool-group {
    margin-top: 0.75rem;
    font-size: 0.6875rem;
    font-weight: 700;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.mcp-tool-group:first-child {
    margin-top: 0;
}

.mcp-server-card__header {
    padding: 1rem 1.25rem 0;
}

.mcp-server-card__name {
    font-weight: 600;
    font-size: 0.95rem;
    margin-bottom: 0.25rem;
}

.mcp-server-card__icon {
    color: #03c04c;
    margin-right: 0.375rem;
}

.mcp-server-card__desc {
    font-size: 0.8125rem;
    color: #6b7280;
    margin: 0;
}

.mcp-server-card__url-row {
    display: flex;
    align-items: center;
    margin: 0.75rem 1.25rem 0;
    background: #f3f4f6;
    border-radius: 6px;
    overflow: hidden;
}

.mcp-server-card__url {
    flex: 1;
    padding: 0.5rem 0.75rem;
    font-size: 0.8125rem;
    color: #111827;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    background: transparent;
}

.mcp-copy-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background: none;
    border: none;
    border-left: 1px solid #e5e7eb;
    color: #6b7280;
    cursor: pointer;
    transition: color 0.15s, background 0.15s;
    flex-shrink: 0;
}

.mcp-copy-btn:hover {
    color: #03c04c;
    background: #e5e7eb;
}

.mcp-server-card__toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0.625rem 1.25rem;
    background: none;
    border: none;
    border-top: 1px solid #f3f4f6;
    margin-top: 0.75rem;
    font-size: 0.8125rem;
    color: #6b7280;
    cursor: pointer;
    transition: color 0.15s;
}

.mcp-server-card__toggle:hover {
    color: #03c04c;
}

.mcp-server-card__tools {
    border-top: 1px solid #f3f4f6;
    padding: 0 1.25rem 0.75rem;
}

.mcp-tool {
    padding: 0.5rem 0;
}

.mcp-tool + .mcp-tool {
    border-top: 1px solid #f9fafb;
}

.mcp-tool__name {
    font-size: 0.8125rem;
    color: #03c04c;
    font-family: SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

.mcp-tool__desc {
    display: block;
    font-size: 0.8125rem;
    color: #6b7280;
    margin-top: 0.125rem;
}

.mcp-tool__desc code {
    font-size: 0.75rem;
    background: #f3f4f6;
    padding: 1px 4px;
    border-radius: 3px;
    color: #1f2937;
}

/* ── Config tabs ── */
.mcp-config-tabs {
    display: inline-flex;
    background: #f3f4f6;
    border-radius: 6px;
    padding: 3px;
}

.mcp-config-tab {
    padding: 0.375rem 1rem;
    border: none;
    background: none;
    border-radius: 4px;
    font-size: 0.8125rem;
    font-weight: 500;
    color: #6b7280;
    cursor: pointer;
    transition: all 0.15s;
}

.mcp-config-tab--active {
    background: #fff;
    color: #111827;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

/* ── Code blocks with copy button ── */
.mcp-code-block {
    position: relative;
}

.mcp-code-block__copy {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid #d1d5db;
    border-radius: 4px;
    color: #6b7280;
    cursor: pointer;
    transition: color 0.15s, border-color 0.15s;
}

.mcp-code-block__copy:hover {
    color: #03c04c;
    border-color: #03c04c;
}

/* ── Transition ── */
.mcp-slide-enter-active,
.mcp-slide-leave-active {
    transition: all 0.2s ease;
    overflow: hidden;
}

.mcp-slide-enter,
.mcp-slide-leave-to {
    opacity: 0;
    max-height: 0;
    padding-top: 0;
    padding-bottom: 0;
}

@media (max-width: 600px) {
    .mcp-auth-cards {
        grid-template-columns: 1fr;
    }
}
</style>
